import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Shield, CheckCircle, AlertCircle, Loader } from 'lucide-react';

export function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get token from URL
        const token = searchParams.get('token');
        const type = searchParams.get('type');

        if (!token || type !== 'signup') {
          throw new Error('Invalid verification link');
        }

        // Verify the token with Supabase
        const { error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'signup'
        });

        if (error) throw error;

        setStatus('success');
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } catch (err) {
        console.error('Verification error:', err);
        setError(err instanceof Error ? err.message : 'Failed to verify email');
        setStatus('error');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Email Verification</h2>
          
          {status === 'verifying' && (
            <div className="mt-4">
              <Loader className="mx-auto h-8 w-8 text-indigo-600 animate-spin" />
              <p className="mt-2 text-gray-600">Verifying your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="mt-4">
              <CheckCircle className="mx-auto h-8 w-8 text-green-500" />
              <p className="mt-2 text-gray-600">
                Your email has been verified successfully!
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Redirecting you to the homepage...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="mt-4">
              <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
              <p className="mt-2 text-red-600">{error}</p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Return to Homepage
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}